/**
 * Forked from @react-three/drei/core/useAnimations.js
 *
 * This is a fork of the useAnimations hook from @react-three/drei.
 * It is a modified version that does not cache actions and does not use lazy actions.
 * This is because we want to ensure that the actions are always up to date and that they are not cached.
 *
 *
 */
import { useFrame } from '@react-three/fiber';
import * as React from 'react';
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  Object3D,
} from 'three';

type Api<T extends AnimationClip> = {
  ref: React.RefObject<Object3D | undefined | null>;
  clips: AnimationClip[];
  mixer: AnimationMixer;
  names: T['name'][];
  actions: { [key in T['name']]: AnimationAction | null };
};

export function useAnimations<T extends AnimationClip>(
  clips: T[],
  root?: React.RefObject<Object3D | undefined | null> | Object3D,
): Api<T> {
  const ref = React.useRef<Object3D>(null);
  const [actualRef] = React.useState(() =>
    root ? (root instanceof Object3D ? { current: root } : root) : ref,
  );
  const [mixer] = React.useState(
    () => new AnimationMixer(undefined as unknown as Object3D),
  );
  React.useLayoutEffect(() => {
    if (root)
      actualRef.current = root instanceof Object3D ? root : root.current;
    (mixer as any)._root = actualRef.current;
  });
  const lazyActions = React.useRef<any>({});
  const api = React.useMemo<Api<T>>(() => {
    const actions = {} as { [key in T['name']]: AnimationAction | null };
    clips.forEach((clip) =>
      Object.defineProperty(actions, clip.name, {
        enumerable: true,
        get() {
          if (actualRef.current) {
            return mixer.clipAction(clip, actualRef.current);
            // Default code before we forked it. Our clips get updated when arm spaces change
            // and this lazyActions was causing cache issues so that the arm space wouldn't update.
            // return (
            //   lazyActions.current[clip.name] ||
            //   (lazyActions.current[clip.name] = mixer.clipAction(
            //     clip,
            //     actualRef.current,
            //   ))
            // );
          }
        },
        configurable: true,
      }),
    );
    return {
      ref: actualRef,
      clips,
      actions,
      names: clips.map((c) => c.name),
      mixer,
    };
  }, [clips]);
  useFrame((state, delta) => mixer.update(delta));
  React.useEffect(() => {
    const currentRoot = actualRef.current;
    return () => {
      // Clean up only when clips change, wipe out lazy actions and uncache clips
      lazyActions.current = {};
      mixer.stopAllAction();
      Object.values(api.actions).forEach((action) => {
        if (currentRoot) {
          mixer.uncacheAction(action as AnimationClip, currentRoot);
        }
      });
    };
  }, [clips]);

  return api;
}

export default useAnimations;
